/** Data-quality routes (IA §2 Manage › Data quality, ADR-0010). Every view's state is in the URL. */
import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { appRoute } from '@/app/appRoute';
import { str } from '@/lib/search';

const oneOf = <T extends string>(v: unknown, allowed: readonly T[]): T | undefined => (allowed.includes(v as T) ? (v as T) : undefined);

export const FINDING_VIEWS = ['live', 'proposed', 'open', 'acknowledged', 'resolved', 'dismissed', 'all'] as const;
export type FindingView = (typeof FINDING_VIEWS)[number];

export interface FindingsSearch {
  status?: FindingView;
  severity?: 'low' | 'medium' | 'high';
  source?: 'deterministic' | 'drift' | 'ai';
  check?: string;
  vehicle?: string;
  device?: string;
  sort?: string;
  selected?: string;
}

export interface TrustSearch {
  state?: 'untrusted' | 'unobserved' | 'degraded' | 'all';
  capability?: string;
  sort?: string;
  selected?: string;
}

export interface HealthSearch {
  q?: string;
  status?: 'issues' | 'ok';
  sort?: string;
}

export const qualityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/quality',
  staticData: { permission: ['quality.finding.read', 'quality.installation.read'] },
  component: lazyRouteComponent(() => import('./pages/OverviewPage')),
});

export const findingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/quality/findings',
  staticData: { permission: 'quality.finding.read' },
  validateSearch: (s: Record<string, unknown>): FindingsSearch => ({
    status: oneOf(s.status, FINDING_VIEWS),
    severity: oneOf(s.severity, ['low', 'medium', 'high'] as const),
    source: oneOf(s.source, ['deterministic', 'drift', 'ai'] as const),
    check: str(s.check),
    vehicle: str(s.vehicle),
    device: str(s.device),
    sort: str(s.sort),
    selected: str(s.selected),
  }),
  component: lazyRouteComponent(() => import('./pages/FindingsPage')),
});

export const findingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/quality/findings/$findingId',
  staticData: { permission: 'quality.finding.read' },
  component: lazyRouteComponent(() => import('./pages/FindingPage')),
});

export const trustRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/quality/trust',
  staticData: { permission: 'quality.finding.read' },
  validateSearch: (s: Record<string, unknown>): TrustSearch => ({
    state: oneOf(s.state, ['untrusted', 'unobserved', 'degraded', 'all'] as const),
    capability: str(s.capability),
    sort: str(s.sort),
    selected: str(s.selected),
  }),
  component: lazyRouteComponent(() => import('./pages/TrustPage')),
});

export const auditsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/quality/audits',
  staticData: { permission: 'quality.finding.read' },
  component: lazyRouteComponent(() => import('./pages/AuditsPage')),
});

export const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/quality/audits/$auditId',
  staticData: { permission: 'quality.finding.read' },
  component: lazyRouteComponent(() => import('./pages/AuditPage')),
});

export const healthRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/quality/devices',
  staticData: { permission: 'quality.installation.read' },
  validateSearch: (s: Record<string, unknown>): HealthSearch => ({ q: str(s.q), status: oneOf(s.status, ['issues', 'ok'] as const), sort: str(s.sort) }),
  component: lazyRouteComponent(() => import('./pages/HealthPage')),
});
